import { useTranslations } from 'next-intl';
import type { PlaylistContentData } from '@/lib/data/loaders/mongodb/playlists';
import type { PlaylistTrack } from '@/lib/data/types';

interface PlaylistTracklistProps {
  playlistContent: PlaylistContentData;
}

const PlaylistTracklist = ({ playlistContent }: PlaylistTracklistProps) => {
  const t = useTranslations('common');
  
  // Filter tracks to only show ones with complete data (name, artist, album all populated)
  const completeTracksOnly = playlistContent.tracks?.filter(track => 
    track.name && 
    track.artists && 
    track.album &&
    track.name.trim() !== '' &&
    track.artists.trim() !== '' &&
    track.album.trim() !== ''
  ) || [];
  
  const tracksToShow = 5;
  const totalTracks = playlistContent.totalTracks || completeTracksOnly.length;

  if (completeTracksOnly.length === 0) {
    return null;
  }

  // Get the title template and replace the playlist name with styled version
  const titleTemplate = t('playlists.pages.detail.components.tracklist.playlistNameTracklistTitle', {
    playlistName: '{PLAYLIST_NAME}'
  });
  
  const titleParts = titleTemplate.split('{PLAYLIST_NAME}');

  return (
    <div className="mt-2 pt-1" data-testid="tracklist-container">
      {/* Section Heading */}
      <h2 className="h3" data-testid="tracklist-title">
        {titleParts[0]}
        <span className="text-gradient-primary">{playlistContent.name}</span>
        {titleParts[1]}
      </h2>
      
      <div
        className="bg-size-cover bg-repeat-0 p-3 p-md-5 mb-4"
        style={{
          backgroundImage: `url(/assets/img/bg-gradient-tracks.avif)`,
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'center',
        }}
        data-testid="tracklist-table-container"
      >
        <table
          className="table table-borderless fs-xs"
          style={{ backgroundColor: 'transparent' }}
          data-testid="tracklist-table"
        >
          <thead>
            <tr>
              <th className="px-0 fw-500 body-color border-bottom" style={{ borderColor: '#353945' }} data-testid="tracklist-header-number">
                {t('playlists.pages.detail.components.tracklist.trackListNumber')}
              </th>
              <th className="px-0 fw-500 body-color border-bottom" style={{ borderColor: '#353945' }} data-testid="tracklist-header-name">
                {t('playlists.pages.detail.components.tracklist.trackListName')}
              </th>
              <th className="px-0 fw-500 body-color border-bottom" style={{ borderColor: '#353945' }} data-testid="tracklist-header-artist">
                {t('playlists.pages.detail.components.tracklist.trackListArtist')}
              </th>
              <th className="px-0 fw-500 body-color border-bottom" style={{ borderColor: '#353945' }} data-testid="tracklist-header-album">
                {t('playlists.pages.detail.components.tracklist.trackListAlbum')}
              </th>
            </tr>
          </thead>
          <tbody data-testid="tracklist-body">
            {completeTracksOnly.slice(0, tracksToShow).map((track, index) => (
              <tr key={index} data-testid={`track-row-${index}`}>
                <th scope="row" data-testid={`track-number-${index}`}>{index + 1}</th>
                <td className="fs-sm fw-500 body-color w-50" data-testid={`track-name-${index}`}>{track.name}</td>
                <td className="fs-sm fw-500 px-0 body-color" data-testid={`track-artist-${index}`}>{track.artists}</td>
                <td className="fs-sm fw-500 body-color" data-testid={`track-album-${index}`}>{track.album}</td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {tracksToShow < totalTracks && (
          <a
            href={playlistContent.spotifyPublicUrl || `https://open.spotify.com/playlist/${playlistContent.spotifyPlaylistId}`}
            className="btn fw-600 d-block w-100 text-white border-0 rounded-4"
            style={{ backgroundColor: '#141416' }}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="view-more-tracks-button"
          >
            {t('playlists.pages.detail.components.tracklist.trackListMoreSongs', {
              remainingTracks: totalTracks - tracksToShow,
            })}
          </a>
        )}
      </div>
    </div>
  );
};

export default PlaylistTracklist; 